// src/hooks/usePaymentGateways.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const KEY = 'paymentGateways';

export const usePaymentGateways = () => {
    return useQuery({
        queryKey: [KEY],
        queryFn: async () => {
            const { data } = await apiClient.get('/payment-gateways');
            return data.data || [];
        },
        staleTime: 1000 * 60,
    });
};

export const useActiveGateway = () => {
    return useQuery({
        queryKey: [KEY, 'active'],
        queryFn: async () => {
            const { data } = await apiClient.get('/payment-gateways/active');
            return data.data || null;
        },
        staleTime: 1000 * 60,
    });
};

export const useMutateGateways = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidate = () => queryClient.invalidateQueries({ queryKey: [KEY] });

    const createGateway = useMutation({
        mutationFn: (payload) => apiClient.post('/payment-gateways', payload),
        onSuccess: () => {
            invalidate();
            setAlert('Gateway criado com sucesso!', 'success');
        },
        onError: (e) => setAlert(e.response?.data?.error || 'Erro ao criar gateway.', 'danger'),
    });

    const updateGateway = useMutation({
        mutationFn: ({ id, ...payload }) => apiClient.put(`/payment-gateways/${id}`, payload),
        onSuccess: () => {
            invalidate();
            setAlert('Gateway atualizado com sucesso!', 'success');
        },
        onError: (e) => setAlert(e.response?.data?.error || 'Erro ao atualizar gateway.', 'danger'),
    });

    const deleteGateway = useMutation({
        mutationFn: (id) => apiClient.delete(`/payment-gateways/${id}`),
        onSuccess: () => {
            invalidate();
            setAlert('Gateway removido com sucesso!', 'success');
        },
        onError: (e) => setAlert(e.response?.data?.error || 'Erro ao remover gateway. Gateways ativos não podem ser excluídos.', 'danger'),
    });

    const toggleStatus = useMutation({
        mutationFn: (id) => apiClient.patch(`/payment-gateways/${id}/toggle-status`),
        onSuccess: () => {
            invalidate();
            setAlert('Status do gateway atualizado!', 'success');
        },
        onError: (e) => setAlert(e.response?.data?.error || 'Erro ao alterar status.', 'danger'),
    });

    const testarGateway = useMutation({
        mutationFn: (id) => apiClient.post(`/payment-gateways/${id}/testar`),
        onError: (e) => setAlert(e.response?.data?.error || 'Erro ao testar gateway.', 'danger'),
    });

    return { createGateway, updateGateway, deleteGateway, toggleStatus, testarGateway };
};
